import { COOP } from "./coop.ts";
import type { AvatarCharacter } from "../avatar-looks.ts";
import type { WeaponFamily } from "../items.ts";
import type { SimpatiaEffects } from "../simpatias.ts";
import { DASH, SKILL_BY_ID, type SkillDef, type SkillShape } from "./skills.ts";

/**
 * As quatro classes (Fase 8, doc 09 §3): uma por personagem da key art. A cor é da classe (F85, ADR 0017:
 * `PALETTE.classes`); o slot é só o número do jogador. Classes repetidas no grupo valem (decisão do dono):
 * quem repete divide a cor e se distingue pelo número e pelo nome.
 */
export const CLASS_IDS = ["baluarte", "lanceiro", "arcanista", "patrulheiro"] as const;
export type ClassId = (typeof CLASS_IDS)[number];

export interface ClassDef {
  readonly id: ClassId;
  readonly name: string;
  readonly role: string;
  readonly fantasy: string;
  readonly character: AvatarCharacter;
  /** Família de arma de afinidade: +15 % de dano com ela (a classe não proíbe nenhuma). */
  readonly affinity: WeaponFamily | "cajado";
  readonly shape: SkillShape;
  readonly skills: { readonly passiva: string; readonly ativa: string; readonly ultimate: string };
  /** O que a classe faz de dia (sobrevivência). */
  readonly perk: string;
}

export const CLASSES: readonly ClassDef[] = [
  {
    id: "baluarte",
    name: "Baluarte",
    role: "Tanque e protetor",
    fantasy: "Veterano de escudo que segurou o convés na tempestade.",
    character: "cavaleiro",
    affinity: "lamina",
    shape: "hexagonos",
    skills: { passiva: "escudo-erguido", ativa: "brado", ultimate: "bastiao" },
    perk: "Couraça: +10 de defesa.",
  },
  {
    id: "lanceiro",
    name: "Lanceiro",
    role: "Escaramuçador",
    fantasy: "Caçador das dunas, rápido e imprudente.",
    character: "barbaro",
    affinity: "lanca",
    shape: "triangulos",
    skills: { passiva: "impeto", ativa: "investida", ultimate: "salto-do-falcao" },
    perk: "Fôlego de corredor: o Fôlego volta 25 % mais rápido.",
  },
  {
    id: "arcanista",
    name: "Arcanista",
    role: "Controle e suporte",
    fantasy: "Estudiosa das runas da névoa.",
    character: "mago",
    affinity: "cajado",
    shape: "circulos",
    skills: { passiva: "ressonancia", ativa: "runa-de-contencao", ultimate: "circulo-da-fenix" },
    perk: "Frugal: a Fome cai 15 % mais devagar.",
  },
  {
    id: "patrulheiro",
    name: "Patrulheiro",
    role: "Dano à distância e reconhecimento",
    fantasy: "Batedor encapuzado da guarda costeira.",
    character: "patrulheiro",
    affinity: "arco",
    shape: "linhas",
    skills: { passiva: "olho-de-falcao", ativa: "flecha-sinalizadora", ultimate: "disparo-rompe-nucleo" },
    perk: "Passo de batedor: +5 % de velocidade.",
  },
];

export const CLASS_BY_ID: ReadonlyMap<ClassId, ClassDef> = new Map(CLASSES.map((c) => [c.id, c]));

export const isClassId = (id: unknown): id is ClassId => typeof id === "string" && (CLASS_IDS as readonly string[]).includes(id);

/** A classe de quem ainda não escolheu (o servidor e o cliente usam a mesma). */
export const DEFAULT_CLASS: ClassId = "baluarte";

/** A classe pelo id; desconhecido (ou vazio), a padrão. */
export function classOf(id: string): ClassDef {
  return CLASS_BY_ID.get(id as ClassId) ?? CLASS_BY_ID.get(DEFAULT_CLASS)!;
}

/** As três habilidades da classe. */
export function classSkills(c: ClassDef): { passiva: SkillDef; ativa: SkillDef; ultimate: SkillDef } {
  const get = (id: string) => SKILL_BY_ID.get(id)!;
  return { passiva: get(c.skills.passiva), ativa: get(c.skills.ativa), ultimate: get(c.skills.ultimate) };
}

/** Dano a mais com a arma de afinidade. */
export const AFFINITY_MUL = 1.15;

/**
 * Os efeitos de Bênçãos com a classe por cima (F64): uma função pura, campo a campo (as regras das
 * Bênçãos misturam soma, multiplicação e tetos; um combinador genérico erraria). Perks de sobrevivência
 * pelos campos que já existem; as passivas de combate em campos próprios, que o combate lê (F65).
 */
export function withClass(effects: SimpatiaEffects, classId: string, arena = false): SimpatiaEffects {
  const c = CLASS_BY_ID.get(classId as ClassId);
  if (!c) return effects;
  // Na Arena (F71), os bônus de combate da classe valem a metade.
  const k = arena ? COOP.arenaClassScale : 1;
  const out: SimpatiaEffects = { ...effects, affinity: c.affinity, affinityMul: 1 + (AFFINITY_MUL - 1) * k };
  switch (c.id) {
    case "baluarte":
      out.defense += 10;
      out.frontGuard = 0.3;
      break;
    case "lanceiro":
      out.staminaRegenMul *= 1.25;
      out.impetus = 0.4 * k;
      out.impetusPosture = 0.25 * k;
      out.dashSpeed = DASH.speed;
      break;
    case "arcanista":
      out.hungerMul *= 0.85;
      out.resonance = 0.4 * k;
      break;
    case "patrulheiro":
      out.speedMul *= 1.05;
      out.hawkEye = 0.2 * k;
      break;
  }
  return out;
}
